// src/services/chat/mediaUploadService.js

import { storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL, uploadString } from 'firebase/storage';

class MediaUploadService {
  constructor() {
    this._storage = storage;
  }

  static getInstance() {
    if (!MediaUploadService._instance) {
      MediaUploadService._instance = new MediaUploadService();
    }
    return MediaUploadService._instance;
  }

  /**
   * Upload an image file to Firebase Storage
   * @param {File} imageFile - The image file to upload
   * @returns {Promise<string>} Download URL of the uploaded image
   */
  async uploadImage(imageFile) {
    try {
      const fileName = `chat_images/${Date.now()}_${imageFile.name || 'image.jpg'}`;
      const storageRef = ref(this._storage, fileName);

      // Compress image if needed (using browser-image-compression or similar)
      // For now, upload as is
      const metadata = {
        contentType: imageFile.type || 'image/jpeg',
      };

      await uploadBytes(storageRef, imageFile, metadata);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Upload an audio file to Firebase Storage
   * @param {File} audioFile - The audio file to upload
   * @param {number} durationSeconds - Duration of the audio in seconds
   * @returns {Promise<string>} Download URL of the uploaded audio
   */
  async uploadAudio(audioFile, durationSeconds) {
    try {
      const fileName = `chat_audio/${Date.now()}_voice.${audioFile.name?.split('.').pop() || 'm4a'}`;
      const storageRef = ref(this._storage, fileName);

      const metadata = {
        contentType: audioFile.type || 'audio/mp4',
        customMetadata: {
          duration: durationSeconds.toString(),
        },
      };

      await uploadBytes(storageRef, audioFile, metadata);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw error;
    }
  }

  /**
   * Upload a video file to Firebase Storage
   * @param {File} videoFile - The video file to upload
   * @returns {Promise<string>} Download URL of the uploaded video
   */
  async uploadVideo(videoFile) {
    try {
      const fileName = `chat_videos/${Date.now()}_${videoFile.name || 'video.mp4'}`;
      const storageRef = ref(this._storage, fileName);

      const metadata = {
        contentType: videoFile.type || 'video/mp4',
      };

      await uploadBytes(storageRef, videoFile, metadata);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  }

  /**
   * Upload a file (generic) to Firebase Storage
   * @param {File} file - The file to upload
   * @param {string} folder - Folder name in storage
   * @returns {Promise<string>} Download URL of the uploaded file
   */
  async uploadFile(file, folder = 'chat_files') {
    try {
      const fileName = `${folder}/${Date.now()}_${file.name || 'file'}`;
      const storageRef = ref(this._storage, fileName);

      const metadata = {
        contentType: file.type || 'application/octet-stream',
      };

      await uploadBytes(storageRef, file, metadata);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Upload a base64 image to Firebase Storage
   * @param {string} base64Data - Base64 encoded image data
   * @param {string} fileName - Name of the file
   * @returns {Promise<string>} Download URL of the uploaded image
   */
  async uploadBase64Image(base64Data, fileName = 'image.jpg') {
    try {
      const storageRef = ref(this._storage, `chat_images/${Date.now()}_${fileName}`);
      
      // Remove data URL prefix if present
      let base64String = base64Data;
      if (base64Data.includes('base64,')) {
        base64String = base64Data.split('base64,')[1];
      }

      const metadata = {
        contentType: 'image/jpeg',
      };

      await uploadString(storageRef, base64String, 'base64', metadata);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading base64 image:', error);
      throw error;
    }
  }

  /**
   * Delete a file from Firebase Storage
   * @param {string} fileUrl - The download URL or full path of the file
   * @returns {Promise<void>}
   */
  async deleteFile(fileUrl) {
    try {
      // If it's a full URL, extract the path
      let path = fileUrl;
      if (fileUrl.includes('firebasestorage.googleapis.com')) {
        // Parse the path from the URL
        const decodedUrl = decodeURIComponent(fileUrl);
        const match = decodedUrl.match(/\/o\/(.+?)\?/);
        if (match && match[1]) {
          path = match[1];
        }
      }

      const storageRef = ref(this._storage, path);
      await storageRef.delete();
      console.log('File deleted:', path);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Get a download URL for a file reference
   * @param {string} filePath - Path to the file in storage
   * @returns {Promise<string>} Download URL
   */
  async getFileUrl(filePath) {
    try {
      const storageRef = ref(this._storage, filePath);
      const url = await getDownloadURL(storageRef);
      return url;
    } catch (error) {
      console.error('Error getting file URL:', error);
      throw error;
    }
  }

  /**
   * Upload a file from a data URL (blob)
   * @param {Blob} blob - The blob data
   * @param {string} fileName - Name of the file
   * @param {string} folder - Folder name
   * @returns {Promise<string>} Download URL
   */
  async uploadBlob(blob, fileName, folder = 'chat_files') {
    try {
      const storageRef = ref(this._storage, `${folder}/${Date.now()}_${fileName}`);
      
      const metadata = {
        contentType: blob.type || 'application/octet-stream',
      };

      await uploadBytes(storageRef, blob, metadata);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading blob:', error);
      throw error;
    }
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const mediaUploadService = MediaUploadService.getInstance();
export { mediaUploadService };
export default mediaUploadService;